import { z } from 'zod';

/**
 * Validation for **in-app route-relative paths** — the only kind of link the client router and the
 * native deep-link resolver are allowed to `navigate()` to.
 *
 * Operator- and link-supplied URLs (e.g. the `AdminSendNotification` `url`, an inbound Universal /
 * App Link) are untrusted. An absolute URL (`https://evil.com/x`), a protocol-relative URL
 * (`//evil.com/x`), or another scheme (`javascript:…`) must never reach `navigate()`: on web it can
 * redirect cross-origin, and on native it silently fails to resolve to a screen. Parsing such a value
 * here, at the boundary, keeps every stored/forwarded link a path the router can actually open on
 * both platforms (ELARA-699).
 *
 * NOTE: This module is platform-neutral (no DOM/Node-only APIs) so it bundles cleanly for the web
 * client, the server, and React Native / Metro alike.
 */

/**
 * Type guard: is `value` a safe, in-app route-relative path?
 *
 * Accepts a path that starts with a single `/` and carries no cross-origin or injection vectors.
 * Query strings and hash fragments are allowed (`/trip/123?tab=hotels#preview=hotel&id=x`); any
 * literal whitespace or control character must be percent-encoded first.
 *
 * @example
 * isRouteRelativePath('/trip/123');        // true
 * isRouteRelativePath('//evil.com');       // false (protocol-relative)
 * isRouteRelativePath('https://evil.com'); // false (absolute URL)
 * isRouteRelativePath('trip/123');         // false (not root-relative)
 */
export function isRouteRelativePath(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value.length === 0) return false;
  if (value[0] !== '/') return false; // must be root-relative
  if (value[1] === '/') return false; // reject protocol-relative ("//host")
  if (value.includes('\\')) return false; // backslashes are normalized to "/" by browsers/UAs
  // Reject any C0 control char, space, or DEL (0x7F). A legal path percent-encodes these, so a
  // literal occurrence signals a malformed or smuggled link. Scanned by code point to keep control
  // characters out of this source file entirely.
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (code <= 0x20 || code === 0x7f) return false;
  }
  return true;
}

/**
 * Coerce an arbitrary value to a safe route-relative path, falling back to `fallback` (default `/`)
 * when it isn't one. Useful for self-healing reads of legacy/operator data.
 *
 * @example
 * normalizeRoutePath('/trip/123');        // '/trip/123'
 * normalizeRoutePath('https://evil.com'); // '/'
 * normalizeRoutePath(undefined, '/home'); // '/home'
 */
export function normalizeRoutePath(value: unknown, fallback = '/'): string {
  return isRouteRelativePath(value) ? value : fallback;
}

/**
 * Zod schema for an in-app route-relative path. Use at flow/model boundaries that accept a
 * navigable URL (e.g. `RouteRelativePathSchema.default('/')`).
 */
export const RouteRelativePathSchema = z.string().refine(isRouteRelativePath, {
  message:
    'Must be an in-app route-relative path starting with "/" (e.g. "/trip/123"). Absolute URLs, protocol-relative URLs ("//host"), and other schemes (e.g. "javascript:") are not allowed.',
});

export type RouteRelativePath = z.infer<typeof RouteRelativePathSchema>;
